import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Users, Clock, CreditCard } from 'lucide-react';
import { servicesData } from '../data/mockData';

const AUTradelinesPage = () => {
  // Filter only AU tradelines
  const auTradelines = servicesData.filter(service => 
    service.title.includes('AU Tradeline')
  );

  // Group by position
  const position2nd = auTradelines.filter(t => t.features.some(f => f.includes('2nd Position')));
  const position3rd = auTradelines.filter(t => t.features.some(f => f.includes('3rd Position')));
  const position4th = auTradelines.filter(t => t.features.some(f => f.includes('4th Position')));
  const position5th = auTradelines.filter(t => t.features.some(f => f.includes('5th Position')));

  const renderTradelineCard = (tradeline) => (
    <div key={tradeline.id} className="tradeline-card au-card" data-testid="au-tradeline-card">
      <div className="tradeline-card-header">
        <h3 className="tradeline-card-title">{tradeline.title}</h3>
      </div>
      
      <p className="tradeline-card-description">{tradeline.description}</p>
      
      <ul className="tradeline-card-features">
        {tradeline.features.slice(0, 5).map((feature, idx) => (
          <li key={idx} className="tradeline-feature">
            <CheckCircle2 size={16} />
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      <div className="tradeline-card-footer">
        {tradeline.paymentLink ? (
          <a 
            href={tradeline.paymentLink} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="btn-primary tradeline-buy-btn"
            data-testid="buy-now-btn"
          >
            Learn More
            <ArrowRight size={18} />
          </a>
        ) : (
          <Link to="/contact" className="btn-secondary tradeline-apply-btn">
            Apply Now
            <ArrowRight size={18} />
          </Link>
        )}
      </div>
    </div>
  );

  return (
    <div className="dark-page tradelines-page au-page">
      {/* Hero Section */}
      <section className="tradelines-hero au-hero">
        <div className="tradelines-hero-content">
          <div className="tradelines-badge au-badge">AUTHORIZED USER TRADELINES</div>
          <h1 className="display-large tradelines-title">
            Boost Your Score with <span className="text-gradient-purple">Seasoned Tradelines</span>
          </h1>
          <p className="body-large tradelines-subtitle">
            Get added to established credit accounts with perfect payment history. 
            AU tradelines can quickly boost your credit score by adding positive history to your profile.
          </p>
          
          <div className="tradelines-features-row">
            <div className="tradeline-feature-item">
              <Users size={24} />
              <span>Authorized User Status</span>
            </div>
            <div className="tradeline-feature-item">
              <CreditCard size={24} />
              <span>Premium Banks (AMEX, Chase, Citi)</span>
            </div>
            <div className="tradeline-feature-item">
              <Clock size={24} />
              <span>Reports to All 3 Bureaus</span>
            </div>
          </div>
        </div>
      </section>

      {/* Guarantee Banner */}
      <section className="guarantee-banner purple-banner">
        <div className="guarantee-content">
          <CheckCircle2 size={32} />
          <p>All tradelines guaranteed to post within 2 billing cycles or your money back</p>
        </div>
      </section>

      {/* Position Explanation */}
      <section className="position-explainer">
        <div className="section-header">
          <h2 className="display-medium section-title">Understanding Reporting Positions</h2>
          <p className="body-medium section-description">
            The position determines how quickly the tradeline reports to bureaus. Lower positions report faster.
          </p>
        </div>
        <div className="position-grid">
          <div className="position-card">
            <div className="position-number">2nd</div>
            <div className="position-label">Fastest Reporting</div>
          </div>
          <div className="position-card">
            <div className="position-number">3rd</div>
            <div className="position-label">Fast Reporting</div>
          </div>
          <div className="position-card">
            <div className="position-number">4th</div>
            <div className="position-label">Standard Reporting</div>
          </div>
          <div className="position-card">
            <div className="position-number">5th</div>
            <div className="position-label">Value Option</div>
          </div>
        </div>
      </section>

      {/* 2nd Position Tradelines */}
      {position2nd.length > 0 && (
        <section className="tradelines-grid-section">
          <div className="section-header">
            <h2 className="display-medium section-title">2nd Position Tradelines</h2>
            <p className="body-medium section-description">Fastest reporting - ideal for time-sensitive credit needs</p>
          </div>
          <div className="tradelines-grid">
            {position2nd.map(renderTradelineCard)}
          </div>
        </section>
      )}

      {/* 3rd Position Tradelines */}
      {position3rd.length > 0 && (
        <section className="tradelines-grid-section">
          <div className="section-header">
            <h2 className="display-medium section-title">3rd Position Tradelines</h2>
            <p className="body-medium section-description">Fast reporting with excellent value</p>
          </div>
          <div className="tradelines-grid">
            {position3rd.map(renderTradelineCard)}
          </div>
        </section>
      )}

      {/* 4th Position Tradelines */}
      {position4th.length > 0 && (
        <section className="tradelines-grid-section">
          <div className="section-header">
            <h2 className="display-medium section-title">4th Position Tradelines</h2>
            <p className="body-medium section-description">Standard reporting timeline</p>
          </div>
          <div className="tradelines-grid">
            {position4th.map(renderTradelineCard)}
          </div>
        </section>
      )}

      {/* 5th Position Tradelines */}
      {position5th.length > 0 && (
        <section className="tradelines-grid-section">
          <div className="section-header">
            <h2 className="display-medium section-title">5th Position Tradelines</h2>
            <p className="body-medium section-description">Best value for building long-term credit</p>
          </div>
          <div className="tradelines-grid">
            {position5th.map(renderTradelineCard)}
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="tradelines-cta-section">
        <div className="tradelines-cta-content">
          <h2 className="display-medium">Need Help Choosing the Right Tradeline?</h2>
          <p className="body-large">Our credit strategists will analyze your profile and recommend the perfect AU tradeline combination.</p>
          <a 
            href="https://calendly.com/thecollinsacademynew/30min" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="btn-primary"
          >
            Schedule Consultation
            <ArrowRight size={20} />
          </a>
        </div>
      </section>
    </div>
  );
};

export default AUTradelinesPage;
